/**
 * Aviron Design Tokens — extracted from Pencil design system.
 * Single source of truth for colors, spacing, typography, and component styles.
 */

// ─── Color Palette ───────────────────────────────────────────────────────────

export const colors = {
  /** Primary brand orange */
  primary: {
    main: '#FF6720',
    hover: '#F5490F',
    active: '#D93D0D',
    light: '#FF8D66',
    contrastText: '#FFFFFF',
  },

  /** Secondary blue */
  secondary: {
    main: '#2F6BFF',
    light: '#82A6FF',
    dark: '#1A4FD9',
    contrastText: '#FFFFFF',
  },

  /** Sidebar / dark surfaces */
  sidebar: {
    bg: '#1A1A2E',
    text: '#8B9DB5',
    textActive: '#FAF8F6', // tinted off-white, never pure #fff
    divider: 'rgba(255, 255, 255, 0.1)', // #FFFFFF1A
    hoverBg: 'rgba(255, 255, 255, 0.08)',
  },

  /** Content area backgrounds */
  background: {
    page: '#F7F8FA',
    paper: '#FFFFFF',
    tableHead: '#F9FAFB',
    tableRowAlt: '#FAFAFA',
    darkPage: '#161616',
    darkPaper: '#232323',
  },

  /** Text colors */
  text: {
    primary: '#111111',
    secondary: '#6B7280',
    tertiary: '#939393',
    disabled: '#A3A3A3',
    light: '#E6E6E6',
    // Dark mode
    darkPrimary: '#E6E6E6',
    darkSecondary: '#ABABAB',
  },

  /** Borders */
  border: {
    light: '#E5E7EB',
    normal: '#CCCCCC',
    dark: '#3C3A39',
  },

  /** Status colors + background tints */
  status: {
    online: { color: '#00BC3C', bg: '#E6F8EC', bgLight: '#E8FFF0' },
    away: { color: '#E6940A', bg: '#FFF4E0' },
    offline: { color: '#666666', bg: '#F4F4F4' },
    pending: { color: '#F3A72E', bg: '#FEF6E9' },
    blocked: { color: '#E60A32', bg: '#FDE7EB' },
  },

  /** Rollout lifecycle status tints */
  rolloutStatus: {
    active: { color: '#00BC3C', bg: '#E6F8EC' },
    draft: { color: '#1A4FD9', bg: '#EAF0FF' },
    archived: { color: '#5A636E', bg: '#F4F4F4' },
  },

  /** Package type tints (APK / OS / FW) */
  packageType: {
    apk: { color: '#1976D2', bg: '#E3F2FD' },
    os: { color: '#E65100', bg: '#FFF3E0' },
    fw: { color: '#00BC3C', bg: '#E6F8EC' },
  },

  /** Semantic colors */
  success: { main: '#00BC3C', light: '#E6F8EC', dark: '#009B32' },
  error: { main: '#E60A32', light: '#FDE7EB', dark: '#AF0725' },
  warning: { main: '#F3A72E', light: '#FEF6E9', dark: '#D4910A' },
  info: { main: '#2F6BFF', light: '#EAF0FF', dark: '#1A4FD9' },

  /** Neutral greys */
  grey: {
    50: '#F4F4F4',
    100: '#E6E6E6',
    200: '#CCCCCC',
    300: '#A3A3A3',
    400: '#939393',
    500: '#808080',
    600: '#666666',
    700: '#4F4F4F',
    800: '#3C3A39',
    900: '#232323',
  },
} as const;

// ─── Typography ──────────────────────────────────────────────────────────────

export const typography = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  /** From Pencil design — pixel sizes used in components */
  fontSize: {
    xs: 11,
    sm: 12,
    base: 13,
    md: 14,
    lg: 15,
    xl: 22,
    '2xl': 28,
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const;

// ─── Spacing & Sizing ────────────────────────────────────────────────────────

export const spacing = {
  /** Sidebar width */
  sidebarWidth: 240,
  sidebarCollapsedWidth: 72,
  /** Content padding */
  pagePadding: 32,
  /** Card padding from Pencil */
  cardPadding: 20,
  /** Nav item padding */
  navItemPaddingY: 10,
  navItemPaddingX: 12,
  /** Nav gap between items */
  navItemGap: 2,
} as const;

// ─── Border Radius ───────────────────────────────────────────────────────────

export const radius = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 16,
  pill: 20,
} as const;

// ─── Shadows ─────────────────────────────────────────────────────────────────

export const shadows = {
  card: '0 1px 3px rgba(0, 0, 0, 0.05)',
  cardHover: '0 4px 12px rgba(0, 0, 0, 0.1)',
} as const;

// ─── Surfaces ────────────────────────────────────────────────────────────────

/**
 * Bordered surface panel (MUI `sx`) used for tables, filter bars, and summary
 * cards across pages. Spread into a Box/Paper `sx` and extend as needed, e.g.
 * `sx={{ ...panelSx, p: 2 }}`.
 */
export const panelSx = {
  bgcolor: 'background.paper',
  borderRadius: `${radius.lg}px`,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 1,
} as const;

// ─── Component Styles (reusable across MUI & Tailwind) ───────────────────────

export const components = {
  /** Stat card on dashboard */
  statCard: {
    borderRadius: radius.lg,
    border: `1px solid ${colors.border.light}`,
    padding: spacing.cardPadding,
    shadow: shadows.card,
  },

  /** Table styles */
  table: {
    headBg: colors.background.tableHead,
    headFontSize: typography.fontSize.xs,
    headFontWeight: typography.fontWeight.semibold,
    headColor: colors.text.secondary,
    rowHeight: 52,
    cellFontSize: typography.fontSize.base,
    borderColor: colors.border.light,
    rowAltBg: colors.background.tableRowAlt,
  },

  /** Nav item */
  navItem: {
    height: 40,
    borderRadius: radius.md,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    activeBg: colors.primary.main,
    activeColor: colors.sidebar.textActive,
    inactiveColor: colors.sidebar.text,
    hoverBg: colors.sidebar.hoverBg,
  },

  /** Live badge */
  liveBadge: {
    bg: colors.status.online.bgLight,
    color: colors.status.online.color,
    borderRadius: radius.pill,
    fontSize: typography.fontSize.sm,
  },

  /** Page header */
  pageHeader: {
    titleSize: typography.fontSize.xl,
    titleWeight: typography.fontWeight.bold,
    subtitleSize: typography.fontSize.base,
    subtitleColor: colors.text.secondary,
  },
} as const;
